import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';

const PAGE_SIZE = 15;

const EMPTY_FILTERS = {
  departmentId: '',
  username: '',
};

const TABLE_COLUMNS = [
  '部門別',
  '用戶登錄名',
  'IP 地址',
  '谷歌驗證碼',
  '安全密碼',
  '創建時間',
  '操作',
];

function buildPageParams(pageNo, filters) {
  return {
    pageNo,
    pageSize: PAGE_SIZE,
    ...(filters.departmentId?.trim()
      ? { departmentId: Number(filters.departmentId.trim()) }
      : {}),
    ...(filters.username?.trim() ? { username: filters.username.trim() } : {}),
  };
}

function formatYesNo(value) {
  return value ? '是' : '否';
}

function formatGoogleState(value) {
  return value === 1 ? '是' : '否';
}

export default function GoogleCaptchaPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.adminGooglePage(buildPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  async function handleConfirmReset() {
    if (!resetTarget?.id) {
      return;
    }
    setResetting(true);
    setError('');
    try {
      await adminApi.resetAdminGoogleState({ id: resetTarget.id });
      setResetTarget(null);
      setMessage(`已重置 ${resetTarget.username} 的谷歌驗證`);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '重置失敗');
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>谷歌驗證碼</h2>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="googleFilterDepartmentId">部門別</label>
          <input
            id="googleFilterDepartmentId"
            type="text"
            value={filters.departmentId}
            onChange={(e) => updateFilter('departmentId', e.target.value)}
            placeholder="部門 ID"
          />
        </div>
        <div className="form-field">
          <label htmlFor="googleFilterUsername">後台權限帳號</label>
          <input
            id="googleFilterUsername"
            type="text"
            value={filters.username}
            onChange={(e) => updateFilter('username', e.target.value)}
            placeholder="請輸入帳號"
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜尋
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">
        共 {total} 筆
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{row.departmentName || row.departmentId || '—'}</td>
                  <td>{row.username || '—'}</td>
                  <td>{row.lastLoginIp || '—'}</td>
                  <td>{formatGoogleState(row.googleState)}</td>
                  <td>{formatYesNo(row.hasSafePwd)}</td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => setResetTarget(row)}
                    >
                      重置谷歌驗證
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next, filters)} />

      <ConfirmDialog
        open={Boolean(resetTarget)}
        title="重置谷歌驗證"
        message={`確定要重置「${resetTarget?.username || ''}」的谷歌驗證狀態嗎？`}
        confirmLabel="重置"
        loading={resetting}
        onConfirm={handleConfirmReset}
        onCancel={() => setResetTarget(null)}
      />
    </div>
  );
}
